import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobCluster } from '~/shared/data-contract/job-cluster';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';
import { updateClustersForFamily } from './use-families-with-clusters';

export type UsePatchJobClusterParams = {
    readonly jobCluster: JobCluster;
};

export type UsePatchJobClusterResult = UseMutationResult<JobCluster, Error, UsePatchJobClusterParams>;

export const usePatchJobCluster = (): UsePatchJobClusterResult => {
    const { patchJobCluster } = useApplicationFetchers();

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobCluster }: UsePatchJobClusterParams) => {
            const result = await patchJobCluster({
                jobCluster,
            });
            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params) => {
            const { architectureId } = params.jobCluster;
            const parentId = params.jobCluster.parentId!;

            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(architectureId),
            });

            updateClustersForFamily(queryClient, architectureId, parentId, (clusters) =>
                clusters.map((cluster) =>
                    cluster.id === params.jobCluster.id
                        ? {
                              ...cluster,
                              description: params.jobCluster.description,
                              title: params.jobCluster.title,
                          }
                        : cluster,
                ),
            );
        },
        onSuccess: (_, { jobCluster }) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(jobCluster.architectureId),
            });
        },
        throwOnError: false,
    });
};
